import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import emailValidator from 'email-validator';
import { ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import InputField from 'Components/InputField';
const { EVENTS } = analytics;

const EmailValidation = ({
  autoFocus,
  continueText,
  disabled,
  emailText,
  errorText,
  failureMessage,
  looped,
  onSubmit,
  transitionTo,
  uim,
  validText,
  disallowRegistration,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [validForm, setValidForm] = useState(false);
  const [value, setValue] = useState('');

  const isValidEmail = (email = '') => {
    const valid = emailValidator.validate(email.trim());
    const message = valid ? '' : validText;
    return {
      valid,
      message,
    };
  };

  const isValidForm = () => {
    const check = isValidEmail(value).valid && !submitting;
    setValidForm(check);
  };

  // When input value changes, check if form is valid
  useEffect(isValidForm, [value]);

  const handleChange = (event) => {
    const value = (event?.target?.value ?? '').trim();
    setValue(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    analytics.event({ action: EVENTS.submit, label: 'continue' });
    onSubmit();
    setSubmitting(true);
    checkIfEmailAvailable(value);
  };

  const checkIfEmailAvailable = async (email) => {
    const rsp = await uim.isEmailAvailable(email);

    if (rsp.available) {
      if (disallowRegistration) {
        transitionTo(ROUTES.PAGE_NOTIFICATION.url, {
          notificationMessageStringKey: 'registrationDisallowed',
          notificationIcon: 'error',
        });
      } else {
        // A: the email is available: register page
        transitionTo(ROUTES.PAGE_REGISTER.url, {
          email,
        });
      }
    } else if (looped) {
      // Looped experience lands on a page welcoming user if the user already has an account
      transitionTo(ROUTES.WELCOME_BACK.url);
    } else {
      // B: the email is already taken
      const blockingAccount = rsp?.blockingAccount ?? {
        hasNative: false,
        providers: [],
        loginBlocked: false,
      };
      if (blockingAccount.providers.length > 0 && !blockingAccount.hasNative) {
        // B-1: the email is linked to a social provider and has not set a password: social login
        transitionTo(ROUTES.PAGE_SOCIAL_LOGIN.url, {
          email,
          providers: blockingAccount.providers,
        });
      } else if (blockingAccount.loginBlocked) {
        // B-2: the login is blocked for some reasons: error message
        // console.log('Login is blocked!', rsp);
        failureMessage(errorText);
      } else if (blockingAccount.hasNative) {
        // B-3: the email is a native account: password page
        transitionTo(ROUTES.PAGE_LOGIN.url, {
          notificationMessage: '',
          failedLogin: false,
          email,
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="email-form">
      <InputField
        id="email"
        name="email"
        label={emailText}
        type="email"
        onChange={handleChange}
        onBlur={handleChange}
        isValid={isValidEmail}
        autoFocus={autoFocus}
        disabled={disabled || submitting}
      />

      <button type="submit" disabled={disabled || !validForm || submitting}>
        {continueText}
      </button>
    </form>
  );
};

EmailValidation.propTypes = {
  autofocus: PropTypes.bool,
  continueText: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  emailText: PropTypes.string.isRequired,
  errorText: PropTypes.string.isRequired,
  failureMessage: PropTypes.func.isRequired,
  looped: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  uim: PropTypes.object.isRequired,
  validText: PropTypes.string.isRequired,
};

EmailValidation.defaultProps = {
  autoFocus: false,
  disabled: false,
  looped: false,
};

export default EmailValidation;
